import React, { useMemo } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import classNames from 'classnames';
import {
  DSR_MENTION_SELECTOR,
  MENTION_ATTRIBUTES,
  STYLE_SELECT_OPTIONS,
  buildContentStyle,
  useRichTextEditorStyles,
} from './helper';
import { ConversationMentionType } from '../../enums/Conversations';
import { TINY_MCE_SRC } from '../../config/env';
import { CollaboratorType } from '../../enums/CollaboratorType';
import { SEARCH_DEBOUNCE_TIMEOUT } from '../../enums/Constraints';
import { debounce } from '../../lib/Debounce';

export type InsertMentiontype = (mentionData: MentionData) => void;

export interface MentionData {
  id: string;
  description?: string;
  entity?: ConversationMentionType | CollaboratorType;
  image?: string;
  name: string;
  term?: string;
}

export interface MentionProps {
  allowInvitations?: boolean;
  enabled: boolean;
  initialMention?: MentionData;
  onInvite?: (input: string, onInsertMention: InsertMentiontype) => void;
  rteEnabled?: boolean;
  searcher: (term: string) => Promise<MentionData[]>;
}

interface QueryMention {
  term: string;
}

export interface RichTextEditorProps {
  additionalPlugins?: string[];
  disableAutoFocus?: boolean;
  disabled?: boolean;
  editorRef: React.MutableRefObject<TinyMCEEditor | undefined>;
  editorSize?: number;
  error?: React.ReactNode;
  initialTextValue?: string;
  label?: string;
  maxSize?: number;
  mentions?: MentionProps;
  onChangeHandler?: (editorValue: string) => void;
  onEditorReady?: () => void;
  placeholder?: string;
  resizeMargin?: number;
  toolbarType: string;
  dataTestId?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = (
  props: RichTextEditorProps
) => {
  const classes = useRichTextEditorStyles();
  const theme = useTheme();

  const {
    additionalPlugins = [],
    disableAutoFocus = false,
    disabled = false,
    editorRef,
    editorSize,
    error,
    initialTextValue,
    label,
    maxSize,
    onChangeHandler,
    onEditorReady,
    placeholder = '',
    resizeMargin = 50,
    toolbarType = '',
    mentions,
    dataTestId,
  } = props;

  /** aadditional plugins are those needed for specific use cases
   * (e.g.: autoresize for conversations) */
  const plugins = useMemo(() => {
    let list = [...additionalPlugins];

    if (mentions?.enabled) {
      list = [...list, 'mentions'];
    }

    return [
      'advlist',
      'autolink',
      'charmap',
      'emoticons',
      'link',
      'lists',
      'table',
    ].concat(list);
  }, [additionalPlugins, mentions?.enabled]);

  const mentionConfiguration = () => {
    if (!mentions?.enabled) {
      return {};
    }

    // keep this variable inside of the context to handle the debounce of mention searcher
    let timeoutId: NodeJS.Timeout;

    return {
      mentions_item_type: 'profile',
      mentions_selector: DSR_MENTION_SELECTOR,
      mentions_min_chars: 0,
      mentions_menu_complete: (
        editor: TinyMCEEditor,
        mentionInfo: MentionData
      ) => {
        const span = editor.getDoc().createElement('span');
        span.className = 'dsr-mention';
        if (mentionInfo.id === '0' && mentions.onInvite) {
          const onInsertMention = (mentionData: MentionData) => {
            insertMention(editor, mentionData);
          };

          mentions.onInvite(mentionInfo.term!, onInsertMention);
          return span;
        }

        span.setAttribute(MENTION_ATTRIBUTES.ENTITY_ID, mentionInfo.id);
        span.setAttribute(MENTION_ATTRIBUTES.ENTITY_TYPE, mentionInfo.entity!);
        span.appendChild(
          editor.getDoc().createTextNode(`@${mentionInfo.name}`)
        );

        // hack to add a space after the mention
        setTimeout(() => {
          editor.execCommand('mceInsertContent', false, ' ');
        }, 50);

        return span;
      },
      mentions_fetch: async (
        query: QueryMention,
        success: (sucess: MentionData[]) => void
      ) => {
        timeoutId = debounce(
          async () => {
            const data = await mentions.searcher(query.term);

            // add empty option for add new contact
            if (mentions.allowInvitations && !data.length) {
              data.push({
                id: '0',
                name: `Invite ${query.term}?`,
                term: query.term,
              });
            }

            success(data);
          },
          SEARCH_DEBOUNCE_TIMEOUT,
          timeoutId
        );
      },
      toolbar: `mention | ${toolbarType}`,
    };
  };

  const toolBarMention = (editor: TinyMCEEditor) => {
    if (!mentions?.enabled) {
      return;
    }

    return editor.ui.registry.addButton('mention', {
      onAction: () => {
        const editable = editor.getBody();
        editor.execCommand('mceInsertContent', false, '@');

        // Simulate a keypress event with a specific key
        const keypressEvent = new KeyboardEvent('keypress', {
          key: '@',
          bubbles: true,
          cancelable: true,
        });
        // dispatch keypressEvent to trigger the mention list
        editable.dispatchEvent(keypressEvent);
      },
      text: '@',
      tooltip: 'Mention @',
    });
  };

  const insertMention = (editor: TinyMCEEditor, mention: MentionData) => {
    const { id, entity, name } = mention;
    const spanHtml = `<span class="dsr-mention" contenteditable="false" ${MENTION_ATTRIBUTES.ENTITY_ID}="${id}" ${MENTION_ATTRIBUTES.ENTITY_TYPE}="${entity}" data-mce-mentions-id="${id}" >@${name}</span> `;

    editor.execCommand('mceInsertContent', false, spanHtml);
    editor.focus();
  };

  const initialMention = (editor: TinyMCEEditor) => {
    if (!(mentions?.enabled && mentions.initialMention)) {
      return;
    }

    insertMention(editor, mentions.initialMention);
  };

  return (
    <Box>
      {label && (
        <Typography
          color="textSecondary"
          variant="caption"
          style={{ marginLeft: '2px' }}
        >
          {label}
        </Typography>
      )}
      <Box
        className={classNames(classes.textContainer, {
          [classes.textContainerError]: error,
        })}
        data-testid={dataTestId}
      >
        <Editor
          tinymceScriptSrc={TINY_MCE_SRC}
          disabled={disabled}
          init={{
            advlist_bullet_styles: 'square',
            advlist_number_styles: 'default',
            auto_focus: disableAutoFocus ? undefined : true,
            autoresize_bottom_margin: resizeMargin,
            branding: false,
            browser_spellcheck: true,
            content_style: buildContentStyle(theme),
            contextmenu: false,
            convert_urls: false,
            elementpath: false,
            height: editorSize,
            link_assume_external_targets: 'https',
            link_title: false,
            max_height: maxSize || editorSize,
            menubar: false,
            placeholder: `${placeholder}`,
            plugins,
            statusbar: false,
            style_formats: STYLE_SELECT_OPTIONS,
            table_toolbar:
              'tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tabledelete ',
            setup: (editor) => {
              editor.ui.registry.addMenuButton('inserttable', {
                icon: 'table',
                fetch: (callback) =>
                  callback([
                    {
                      type: 'fancymenuitem',
                      fancytype: 'inserttable',
                      onAction: (data) => {
                        editor.execCommand('mceInsertTable', false, {
                          rows: data.numRows,
                          columns: data.numColumns,
                        });
                      },
                    },
                  ]),
              });

              toolBarMention(editor);
            },
            link_target_list: false,
            paste_as_text: true,
            paste_data_images: false,
            resize: false,
            toolbar: toolbarType,
            quickbars_insert_toolbar: false,
            ...mentionConfiguration(),
          }}
          onEditorChange={onChangeHandler}
          onInit={(_evt, editor) => {
            editorRef.current = editor;
            if (onEditorReady) {
              onEditorReady();
            }
            initialMention(editor);
          }}
          value={initialTextValue}
        />
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default RichTextEditor;
